import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {ChevronLeft, ChevronRight, X, Car, ZoomIn, Images} from 'lucide-react'

interface ImageGalleryProps {
  mainImage?: string
  images?: string[]
  alt: string
  className?: string
  height?: string
  showThumbnails?: boolean
  showCounter?: boolean
  vehicleId?: string
}

export default function ImageGallery({
  mainImage,
  images = [],
  alt,
  className = '',
  height = 'h-64',
  showThumbnails = false,
  showCounter = true,
  vehicleId,
}: ImageGalleryProps) {
  const navigate = useNavigate()
  const [currentIndex, setCurrentIndex] = useState(0)
  const [lightboxOpen, setLightboxOpen] = useState(false)

  const openPhotosPage = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault()
      e.stopPropagation()
    }
    if (vehicleId) {
      const from = window.location.pathname + window.location.search
      navigate(
        `/vehicule/${vehicleId}/photos?photo=${currentIndex}&from=${encodeURIComponent(from)}`
      )
    } else {
      setLightboxOpen(true)
    }
  }

  // Construire la liste complète des images (principale + galerie)
  const allImages: string[] = []
  if (mainImage) allImages.push(mainImage)
  if (images?.length) {
    images.forEach((img) => {
      if (img && img !== mainImage) allImages.push(img)
    })
  }

  const hasMultiple = allImages.length > 1
  const totalImages = allImages.length

  const goTo = (index: number) => {
    if (index < 0) setCurrentIndex(totalImages - 1)
    else if (index >= totalImages) setCurrentIndex(0)
    else setCurrentIndex(index)
  }

  const goNext = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    goTo(currentIndex + 1)
  }

  const goPrev = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    goTo(currentIndex - 1)
  }

  if (allImages.length === 0) {
    return (
      <div className={`${height} bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center ${className}`}>
        <Car className="w-16 h-16 text-gray-400" />
      </div>
    )
  }

  return (
    <>
      <div className={`relative group ${className}`}>
        {/* Image principale */}
        <img
          src={allImages[currentIndex]}
          alt={`${alt} - Photo ${currentIndex + 1}`}
          className={`w-full ${height} object-cover cursor-pointer`}
          onClick={openPhotosPage}
          draggable={false}
        />

        {/* Overlay zoom */}
        <div
          className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center pointer-events-none"
        >
          <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/60 px-3 py-2 rounded-full flex items-center gap-2">
            {vehicleId ? (
              <>
                <Images className="w-4 h-4 text-white" />
                <span className="text-white text-xs font-bold">Voir toutes les photos</span>
              </>
            ) : (
              <ZoomIn className="w-5 h-5 text-white" />
            )}
          </div>
        </div>

        {/* Flèches de navigation */}
        {hasMultiple && (
          <>
            <button
              onClick={goPrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 backdrop-blur-sm shadow-lg hover:scale-110 z-10"
              aria-label="Photo precedente"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={goNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white p-2 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 backdrop-blur-sm shadow-lg hover:scale-110 z-10"
              aria-label="Photo suivante"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}

        {/* Compteur de photos */}
        {hasMultiple && showCounter && (
          <div className="absolute bottom-3 right-3 bg-black/70 text-white text-xs font-bold px-3 py-1.5 rounded-full backdrop-blur-sm z-10">
            {currentIndex + 1} / {totalImages}
          </div>
        )}

        {/* Points indicateurs */}
        {hasMultiple && totalImages <= 6 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
            {allImages.map((_, idx) => (
              <button
                key={idx}
                onClick={(e) => { e.preventDefault(); e.stopPropagation(); setCurrentIndex(idx) }}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  idx === currentIndex
                    ? 'bg-[#fbbf24] w-6 shadow-lg'
                    : 'bg-white/60 hover:bg-white/90'
                }`}
                aria-label={`Photo ${idx + 1}`}
              />
            ))}
          </div>
        )}
      </div>

      {/* Miniatures sous l'image */}
      {hasMultiple && showThumbnails && (
        <div className="flex gap-2 mt-3 overflow-x-auto pb-2 px-1">
          {allImages.map((img, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentIndex(idx)}
              className={`flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-all duration-300 hover:scale-105 ${
                idx === currentIndex
                  ? 'border-[#fbbf24] shadow-lg shadow-yellow-400/30 scale-105'
                  : 'border-gray-300 opacity-60 hover:opacity-100'
              }`}
            >
              <img src={img} alt={`${alt} miniature ${idx + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {/* Lightbox plein écran */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={() => setLightboxOpen(false)}
        >
          {/* Bouton fermer */}
          <button
            onClick={() => setLightboxOpen(false)}
            className="absolute top-4 right-4 bg-white/10 hover:bg-white/20 text-white p-3 rounded-full transition-all z-50 backdrop-blur-sm"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Image */}
          <div className="relative max-w-5xl max-h-[85vh] w-full" onClick={(e) => e.stopPropagation()}>
            <img
              src={allImages[currentIndex]}
              alt={`${alt} - Photo ${currentIndex + 1}`}
              className="w-full h-full max-h-[85vh] object-contain rounded-lg"
            />

            {/* Navigation lightbox */}
            {hasMultiple && (
              <>
                <button
                  onClick={goPrev}
                  className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/30 text-white p-3 rounded-full transition-all backdrop-blur-sm"
                >
                  <ChevronLeft className="w-8 h-8" />
                </button>
                <button
                  onClick={goNext}
                  className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/10 hover:bg-white/30 text-white p-3 rounded-full transition-all backdrop-blur-sm"
                >
                  <ChevronRight className="w-8 h-8" />
                </button>
              </>
            )}

            {/* Compteur lightbox */}
            {hasMultiple && (
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white text-sm font-bold px-4 py-2 rounded-full backdrop-blur-sm">
                {currentIndex + 1} / {totalImages} photos
              </div>
            )}
          </div>

          {/* Miniatures lightbox */}
          {hasMultiple && (
            <div className="absolute bottom-20 left-1/2 -translate-x-1/2 flex gap-2 max-w-xl overflow-x-auto pb-2 px-4">
              {allImages.map((img, idx) => (
                <button
                  key={idx}
                  onClick={(e) => { e.stopPropagation(); setCurrentIndex(idx) }}
                  className={`flex-shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-all duration-300 ${
                    idx === currentIndex
                      ? 'border-[#fbbf24] shadow-lg shadow-yellow-400/30 scale-110'
                      : 'border-white/20 opacity-50 hover:opacity-100'
                  }`}
                >
                  <img src={img} alt={`Miniature ${idx + 1}`} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </>
  )
}
